import { CircularProgress, List } from "@mui/material";
import React, { useContext } from "react";
import RecipeListItem from "./RecipeListItem";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import inventoryClient from "../../../clients/inventoryClient";
import Store from "../../../Store/Store";
import CTABlock from "../../../components/CTABlock";

const RecipeList = ({
  recipes,
  recipeTags,
  isLoadingPaginatedRecipes,
  isLoadingPaginatedSearchData,
}) => {
  const { establishmentid } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const store = useContext(Store);

  // the list on screen is fed by the browse query or by either search query, so all three have to be
  // busted. Otherwise an edit made while searching only shows up after a manual refresh.
  const recipeListQueryKeys = [
    "recipes-paginated",
    "paginated-recipe-search",
    "paginated-recipe-ingredient-search",
  ];

  // invalidateQueries on its own refetches whichever of these is actually on screen and just marks the
  // rest stale, so they refetch when the user next lands on them. A companion refetchQueries call would
  // instead fire a request for every cached page and every search term still in the cache.
  const invalidateRecipesPaginated = () => {
    recipeListQueryKeys.forEach((key) => {
      queryClient.invalidateQueries({
        queryKey: [
          key,
          {
            establishmentId: establishmentid,
          },
        ],
      });
    });
  };

  const deleteRecipeMutation = useMutation(inventoryClient.deleteRecipe, {
    onSuccess: (_, variables) => {
      invalidateRecipesPaginated();

      queryClient.setQueryData(
        ["recipes", { establishmentId: establishmentid }],
        (prev) => {
          const newRecipes = prev?.recipes?.filter(
            (x) => x.id !== variables.recipeId
          );

          return { recipes: newRecipes };
        }
      );
    },
  });

  const copyRecipeMutation = useMutation(inventoryClient.copyRecipe, {
    onSuccess: (result) => {
      invalidateRecipesPaginated();
      // a duplicate is a brand new recipe, so the full library the ingredient and menu pickers read
      // from is stale, and the copy counts against the freemium recipe limit
      queryClient.invalidateQueries({
        queryKey: ["recipes", { establishmentId: establishmentid }],
      });
      queryClient.invalidateQueries({
        queryKey: ["rate-limits", { establishmentId: establishmentid }],
      });
      navigate(result?.recipe?.id);
    },
  });

  // mutateAsync so the delete modal can actually await the result and close itself. mutate() resolved
  // immediately, which left the modal up whenever the row did not unmount from under it.
  const handleDeleteRecipe = async (recipeId) => {
    const archivePayload = {
      establishmentId: store.currentEstablishment?.id,
      recipeId: recipeId,
    };

    await deleteRecipeMutation.mutateAsync(archivePayload);
  };

  const handleCopyRecipe = async (recipeId) => {
    await copyRecipeMutation.mutateAsync({
      establishmentId: establishmentid,
      recipeId: recipeId,
    });
  };

  return (
    <>
      {isLoadingPaginatedRecipes || isLoadingPaginatedSearchData ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </div>
      ) : (
        <>
          {recipes?.length > 0 ? (
            <List sx={{ width: "100%", marginTop: 4 }}>
              {recipes.map((x) => (
                <RecipeListItem
                  recipe={x}
                  key={x.id}
                  allTags={recipeTags}
                  handleDeleteRecipe={handleDeleteRecipe}
                  handleCopyRecipe={handleCopyRecipe}
                  invalidateParentQueries={invalidateRecipesPaginated}
                />
              ))}
            </List>
          ) : (
            <CTABlock header="No Results Found" hideButton />
          )}
        </>
      )}
    </>
  );
};

export default RecipeList;
